import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Compass, Users, MessageSquare, Activity,
  LogOut, Menu, X, ChevronRight, HeartHandshake, ListChecks, Settings, Timer,
  Award,
  Snowflake,
  Globe2,
  Megaphone,
  Rocket,
  ShieldCheck,
} from "lucide-react";
import { getProfile, getUser, clearUser, getThemePreference, setUser as persistUser, WINTER_ARC_THEME } from "@/lib/storage";
import { getBillingCatalog, getCreatorDashboard, getLikedProfiles, getMatches, getMe, isFrontendWinterArcPreview, STANDALONE_MODE } from "@/lib/api";
import AdSlot from "@/components/app/AdSlot";
import BrandLogo from "@/components/BrandLogo";
import { ICON_MAP } from "@/components/app/IconAvatar";
import NotificationCenter from "@/components/app/NotificationCenter";
import { ADSENSE_SLOTS } from "@/config/adsense";
import { disableWebPush, syncWebPush } from "@/lib/pushNotifications";
import { partnerFirstName, preferredWorkspace, setWorkspaces, useWorkspaces, workspacesFromMatches } from "@/lib/workspaces";

export default function AppShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const workspaces = useWorkspaces();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [open, setOpen] = useState(false);
  const [newMatches, setNewMatches] = useState(0);
  const [likesReceived, setLikesReceived] = useState(0);
  const [previewPagesEnabled, setPreviewPagesEnabled] = useState(false);
  const [theme, setTheme] = useState("default");
  const [creatorApproved, setCreatorApproved] = useState(false);

  useEffect(() => {
    const u = getUser();
    if (!u) return navigate("/auth");
    setUser(u);
    setProfile(getProfile());
    setTheme(getThemePreference(u));
    let cancelled = false;
    getBillingCatalog()
      .then((catalog) => { if (!cancelled) setPreviewPagesEnabled(Boolean(catalog?.features?.previewPages)); })
      .catch(() => { if (!cancelled) setPreviewPagesEnabled(false); });
    getCreatorDashboard().then(result => { if (!cancelled) setCreatorApproved(Boolean(result.approved)); }).catch(() => {});
    if (!STANDALONE_MODE) syncWebPush().catch(() => {});
    const refreshNavigation = async () => {
      try {
        const [matches, liked, freshUser] = await Promise.all([getMatches(), getLikedProfiles(), getMe()]);
        if (!cancelled) {
          persistUser(freshUser);
          setUser(freshUser);
          setTheme(getThemePreference(freshUser));
          setWorkspaces(workspacesFromMatches(matches));
          setNewMatches((matches.matches || []).filter((match) => match.isNew).length);
          setLikesReceived((liked.received || []).length);
        }
      } catch {
        // Page-specific views surface API errors; the shell remains usable if counters cannot refresh.
      }
    };
    refreshNavigation();
    const timer = setInterval(refreshNavigation, 30000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [navigate]);

  useEffect(() => {
    const syncTheme = () => {
      const currentUser = getUser();
      if (currentUser) setTheme(getThemePreference(currentUser));
    };
    window.addEventListener("hm-theme-change", syncTheme);
    return () => window.removeEventListener("hm-theme-change", syncTheme);
  }, []);

  useEffect(() => {
    const syncProfile = (event) => setProfile(event.detail?.profile || getProfile());
    window.addEventListener("hm-profile-change", syncProfile);
    return () => window.removeEventListener("hm-profile-change", syncProfile);
  }, []);

  const logout = async () => {
    try { await disableWebPush(); } catch { /* Signing out must still succeed if push cleanup fails. */ }
    clearUser();
    navigate("/");
  };

  const items = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
    ...(user?.entitlements?.winterArc || user?.entitlements?.winter_arc ? [{ to: "/winter-arc", label: "Winter Arc", icon: Snowflake, testId: "nav-winter-arc" }] : []),
    { to: "/discover", label: "Discover", icon: Compass, testId: "nav-discover" },
    { to: "/liked", label: "Liked", icon: HeartHandshake, badge: likesReceived || null, testId: "nav-liked" },
    { to: "/matches", label: "Matches", icon: Users, badge: newMatches || null, testId: "nav-matches" },
    { to: "/tasks", label: "Tasks", icon: ListChecks, testId: "nav-tasks" },
    { to: "/tools/focus", label: "Tools", icon: Timer, testId: "nav-tools" },
    { to: "/shield", label: "Shield", icon: ShieldCheck, testId: "nav-shield" },
    { to: "/chat", label: "Chat", icon: MessageSquare, testId: "nav-chat" },
    // One partnership is a plain link. With more, every partner gets a row, so no workspace hides behind another.
    ...(workspaces.length === 1 ? [{ to: `/workspace/${workspaces[0].id}`, label: "Workspace", icon: Activity, testId: "nav-workspace" }] : []),
    ...(workspaces.length > 1 ? [{
      to: `/workspace/${preferredWorkspace(workspaces).id}`, label: "Workspaces", icon: Activity, testId: "nav-workspace",
      children: workspaces.map((workspace) => ({ to: `/workspace/${workspace.id}`, label: partnerFirstName(workspace), iconKey: workspace.partner?.profileIcon, pending: workspace.pending, testId: `nav-workspace-${workspace.id}` })),
    }] : []),
    { to: "/flex", label: "Flex", icon: Award, testId: "nav-flex" },
    ...(creatorApproved ? [{ to: "/creators/dashboard", label: "Creator studio", icon: Megaphone, testId: "nav-creators" }] : []),
    { to: "/community", label: "Community", icon: Globe2, testId: "nav-community", disabled: !previewPagesEnabled, comingSoon: true },
  ];

  if (!user) return null;
  const displayName = profile?.fullName || user.fullName || user.name;
  const signedInIdentity = profile?.username ? `@${profile.username}` : displayName;

  return (
    <div className={`min-h-screen bg-[#0A0A0A] text-[#F5F5F0] ${theme === WINTER_ARC_THEME ? "winter-arc-theme" : ""}`}>
      {STANDALONE_MODE && <div className="bg-violet-500/15 border-b border-violet-400/25 px-4 py-1.5 text-center text-[10px] uppercase tracking-[0.2em] text-violet-200" data-testid="standalone-banner">Standalone frontend · mock data · no backend</div>}
      {!STANDALONE_MODE && isFrontendWinterArcPreview() && <div className="bg-white text-black px-4 py-1.5 text-center text-[10px] uppercase tracking-[0.2em]" data-testid="winter-preview-banner">Winter Arc frontend preview · no backend writes</div>}
      <AdSlot
        slot={ADSENSE_SLOTS.header}
        format="horizontal"
        className="min-h-[64px] border-b border-white/5 bg-black px-4 pt-2 sm:min-h-[84px]"
      />
      {/* Top bar (mobile + desktop) */}
      <div className="lg:hidden sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/5">
        <div className="px-4 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <BrandLogo />
            <span className="font-serif-editorial text-lg tracking-tight">HustlrMate</span>
          </Link>
          <div className="flex items-center gap-1"><NotificationCenter /><button onClick={() => setOpen((v) => !v)} data-testid="app-mobile-toggle" className="p-2 text-[#F5F5F0]" aria-label="Toggle menu">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button></div>
        </div>
        {open && (
          <div className="border-t border-white/5 bg-black/95 backdrop-blur-xl px-4 py-4 space-y-1">
            {items.map((it) => (
              it.children ? (
                <WorkspaceNavGroup key="workspaces" item={it} active={location.pathname.startsWith("/workspace/")} onNavigate={() => setOpen(false)} />
              ) : it.disabled ? (
                <div key={it.to} aria-disabled="true" data-testid={it.testId} className="flex cursor-not-allowed items-center gap-3 rounded-md px-3 py-2.5 text-sm text-[#5b5b60] opacity-70">
                  <it.icon className="w-4 h-4" strokeWidth={1.5} />
                  <span className="font-light">{it.label}</span>
                  <span className="ml-auto text-[8px] uppercase tracking-[.16em]">Soon</span>
                </div>
              ) : (
                <NavLink
                  key={it.to}
                  to={it.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm ${
                      isActive ? "bg-[#F59E0B]/10 text-[#F5F5F0]" : "text-[#A1A1AA] hover:text-[#F5F5F0]"
                    }`
                  }
                  data-testid={it.testId}
                >
                  <it.icon className="w-4 h-4" strokeWidth={1.5} />
                  <span className="font-light">{it.label}</span>
                  {it.comingSoon && <span className="ml-auto text-[8px] uppercase tracking-[.16em] text-[#7a7a80]">Soon</span>}
                  {it.badge ? <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F59E0B] text-black font-semibold">{it.badge}</span> : null}
                </NavLink>
              )
            ))}
            <div className="mt-3 flex items-center gap-3 border-t border-white/5 px-3 pt-4">
              <button type="button" onClick={() => { setOpen(false); navigate("/settings/profile"); }} className="min-w-0 flex-1 rounded-md px-2 py-1 text-left text-xs text-[#A1A1AA] transition-colors hover:bg-white/[0.04] hover:text-[#F5F5F0]" aria-label="Open your profile settings" data-testid="signed-in-profile">
                Signed in as
                <div className="truncate font-serif-editorial text-sm text-[#F5F5F0]">{signedInIdentity}</div>
                {profile?.username && displayName && <div className="truncate text-[10px] text-[#777]">{displayName}</div>}
              </button>
              <button
                type="button"
                onClick={() => { setOpen(false); navigate("/settings"); }}
                data-testid="nav-settings"
                className="rounded-md p-2 text-[#A1A1AA] transition-colors hover:bg-white/[0.06] hover:text-[#F5F5F0]"
                aria-label="Open settings"
                title="Settings"
              >
                <Settings className="h-4 w-4" />
              </button>
            </div>
            <button onClick={logout} className="mt-3 flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-[#A1A1AA] w-full">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        )}
      </div>

      <div className="lg:grid lg:grid-cols-[260px_1fr]">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex lg:flex-col lg:sticky lg:top-0 lg:h-screen border-r border-white/5 bg-[#080808]">
          <div className="px-6 h-16 flex items-center border-b border-white/5">
            <Link to="/dashboard" className="flex items-center gap-2">
              <BrandLogo />
              <span className="font-serif-editorial text-xl tracking-tight">HustlrMate</span>
            </Link>
          </div>
          <nav className="flex-1 px-3 py-6 space-y-1">
            <div className="overline px-3 mb-3">Menu</div>
            {items.map((it) => (
              it.children ? (
                <WorkspaceNavGroup key="workspaces" item={it} active={location.pathname.startsWith("/workspace/")} desktop />
              ) : it.disabled ? (
                <div key={it.to} aria-disabled="true" data-testid={it.testId} className="flex cursor-not-allowed items-center gap-3 rounded-md px-3 py-2.5 text-sm text-[#5b5b60] opacity-70">
                  <it.icon className="w-4 h-4" strokeWidth={1.5} />
                  <span className="font-light">{it.label}</span>
                  <span className="ml-auto text-[8px] uppercase tracking-[.16em]">Soon</span>
                </div>
              ) : (
                <NavLink
                  key={it.to}
                  to={it.to}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                      isActive
                        ? "bg-[#F59E0B]/10 text-[#F5F5F0] border-l-2 border-[#F59E0B] pl-[10px]"
                        : "text-[#A1A1AA] hover:text-[#F5F5F0] hover:bg-white/[0.03]"
                    }`
                  }
                  data-testid={it.testId}
                >
                  <it.icon className="w-4 h-4" strokeWidth={1.5} />
                  <span className="font-light">{it.label}</span>
                  {it.comingSoon ? <span className="ml-auto text-[8px] uppercase tracking-[.16em] text-[#7a7a80]">Soon</span> : it.badge ? (
                    <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F59E0B] text-black font-semibold">{it.badge}</span>
                  ) : (
                    <ChevronRight className="ml-auto w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                </NavLink>
              )
            ))}
          </nav>
          <div className="px-3 py-4 border-t border-white/5">
            <div className="mb-2 flex items-center gap-2 px-3">
              <Link to="/settings/profile" className="min-w-0 flex-1 rounded-md px-2 py-1 text-xs text-[#A1A1AA] transition-colors hover:bg-white/[0.04] hover:text-[#F5F5F0]" aria-label="Open your profile settings" data-testid="signed-in-profile">
                Signed in as
                <div className="truncate font-serif-editorial text-base text-[#F5F5F0]">{signedInIdentity}</div>
                {profile?.username && displayName && <div className="truncate text-[10px] text-[#777]">{displayName}</div>}
              </Link>
              <NotificationCenter placement="above" />
              <Link
                to="/settings"
                data-testid="nav-settings"
                className="shrink-0 rounded-md p-2 text-[#A1A1AA] transition-colors hover:bg-white/[0.06] hover:text-[#F5F5F0]"
                aria-label="Open settings"
                title="Settings"
              >
                <Settings className="h-4 w-4" />
              </Link>
            </div>
            <button onClick={logout} data-testid="app-logout" className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs text-[#A1A1AA] hover:text-[#F5F5F0] hover:bg-white/[0.03]">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </aside>

        <main className="min-w-0">{children}</main>
      </div>
    </div>
  );
}

// "Workspaces" with a row per partner. The heading opens the workspace you used last; each row
// opens its own, and the row for the workspace on screen is the one that lights up.
function WorkspaceNavGroup({ item, active, onNavigate, desktop = false }) {
  return (
    <div role="group" aria-label="Workspaces">
      <Link
        to={item.to}
        onClick={onNavigate}
        data-testid={item.testId}
        className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${active ? "text-[#F5F5F0]" : "text-[#A1A1AA] hover:text-[#F5F5F0]"} ${desktop ? "hover:bg-white/[0.03]" : ""}`}
      >
        <item.icon className="w-4 h-4" strokeWidth={1.5} />
        <span className="font-light">{item.label}</span>
        <span className="ml-auto text-[10px] text-[#7a7a80]" aria-label={`${item.children.length} workspaces`}>{item.children.length}</span>
      </Link>
      <div className="ml-5 mt-0.5 space-y-0.5 border-l border-white/10 pl-2">
        {item.children.map((child) => {
          const Icon = ICON_MAP[child.iconKey] || Rocket;
          return (
            <NavLink
              key={child.to}
              to={child.to}
              onClick={onNavigate}
              data-testid={child.testId}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive ? "bg-[#F59E0B]/10 text-[#F5F5F0]" : `text-[#A1A1AA] hover:text-[#F5F5F0] ${desktop ? "hover:bg-white/[0.03]" : ""}`
                }`
              }
            >
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]">
                <Icon className="h-3 w-3" strokeWidth={1.5} />
              </span>
              <span className="truncate font-light">{child.label}</span>
              {child.pending && <span className="ml-auto text-[10px] text-[#7a7a80]">Pending</span>}
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}
